'use client';

import { captureException } from '@sentry/react';
import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type FriendStreamUpdateDto } from '@furality/fox-client/models';
import { useAuth } from '@furality/frontend-stack';
import {
  db,
  type FriendStreamLocation,
  flattenStreamUpdate,
  wipeAllEntries,
  wipeOldEntries,
} from '@/lib/db/friendLocation.db.ts';
import useChannelSubscription from '@/lib/hooks/useChannelSubscription';
import useFlag from '@/lib/hooks/useFlag';
import FriendLocationWorker from '@/lib/workers/friendLocation.worker?worker&url';
import { EVENT_TYPE } from '@/lib/workers/workerTypes';

import { CentrifugeContext } from './CentrifugeProvider';

type FriendLocationInteraction = {
  useFriendsByInstanceId: (instanceId: string) => FriendStreamLocation[] | undefined;
  useFriendsByEventId: (eventId: string) => FriendStreamLocation[] | undefined;
  useActiveFriends: () => FriendStreamLocation[] | undefined;
  reloadConnection: () => void;
};

export const FriendLocationContext = createContext<FriendLocationInteraction>({
  useFriendsByInstanceId: () => undefined,
  useFriendsByEventId: () => undefined,
  useActiveFriends: () => undefined,
  reloadConnection: () => undefined,
});

// fox client uses typebox 1.0 schemas, i'm sad
const UserLocationUserV3DtoSchema = Type.Object({
  userId: Type.String(),
  displayName: Type.String(),
  avatarThumbnail: Type.String(),
});

const UserLocationInstanceV3DtoSchema = Type.Object({
  location: Type.String(),
  instanceName: Type.String(),
  furalityInstanceId: Type.String(),
  furalityEventId: Type.String(),
});

const FriendStreamUpdateDtoSchema = Type.Object({
  user: UserLocationUserV3DtoSchema,
  instance: Type.Optional(Type.Union([UserLocationInstanceV3DtoSchema, Type.Null()])),
});

const isFriendStreamUpdateDto = (data: unknown): data is FriendStreamUpdateDto => {
  return Value.Check(FriendStreamUpdateDtoSchema, data);
};

const RELOAD_EVENT = 'RELOAD_EVENT';

const SSELocationDataProvider: FC<{ events: EventTarget }> = ({ events }) => {
  const worker = useRef<Worker | SharedWorker | null>(null);
  const { auth } = useAuth();

  // Worker handling, creates the worker or shared worker
  useEffect(() => {
    if (worker.current || !auth?.token) {
      return;
    }

    // Common message handler for both worker and shared worker
    const constructMessageHandler = (
      postMessage: (message: unknown) => void,
      close: () => void,
    ) => {
      return (ev: MessageEvent) => {
        const [type, ...data] = ev.data as [EVENT_TYPE, ...unknown[]];
        switch (type) {
          // worker needs auth
          case EVENT_TYPE.CONNECT: {
            postMessage([EVENT_TYPE.CONNECT, auth.token]);
            break;
          }

          // connection error
          case EVENT_TYPE.CONNECT_ERROR: {
            const [message] = data;
            if (typeof message === 'string') {
              console.error('Worker Error', message);
            } else {
              console.error('Worker returned unknown error');
            }
            close();
            break;
          }

          default:
            break;
        }
      };
    };

    if (typeof SharedWorker !== 'undefined') {
      const w = new SharedWorker(FriendLocationWorker, { type: 'module' });
      worker.current = w;
      w.port.onmessage = constructMessageHandler(
        (message: unknown) => w.port.postMessage(message),
        () => w.port.close(),
      );

      // Close worker connection on unmount
      return () => {
        w.port.postMessage([EVENT_TYPE.DISCONNECT]);
        w.port.close();
        worker.current = null;
      };
    }

    // Regular worker
    const w = new Worker(FriendLocationWorker, { type: 'module' });
    worker.current = w;
    w.onmessage = constructMessageHandler(
      (message: unknown) => w.postMessage(message),
      () => w.postMessage([EVENT_TYPE.DISCONNECT]),
    );

    return () => {
      w.postMessage([EVENT_TYPE.DISCONNECT]);
      worker.current = null;
    };
  }, [auth]);

  useEffect(() => {
    const doReload = () => {
      if (typeof SharedWorker !== 'undefined' && worker.current instanceof SharedWorker) {
        worker.current.port.postMessage([EVENT_TYPE.RELOAD]);
      } else if (worker.current instanceof Worker) {
        worker.current.postMessage([EVENT_TYPE.RELOAD]);
      }
    };

    events.addEventListener(RELOAD_EVENT, doReload);

    return () => {
      events.removeEventListener(RELOAD_EVENT, doReload);
    };
  });

  return null;
};

const CentrifugeLocationDataProvider: FC<{ events: EventTarget }> = ({ events }) => {
  const { reloadConnection: reloadCentrifugeConnection } = useContext(CentrifugeContext);

  useChannelSubscription(
    'users-locations:friends',
    async (data) => {
      if (!isFriendStreamUpdateDto(data)) {
        console.error('Received invalid friend stream update data', data);
        return;
      }

      const flat = flattenStreamUpdate(data);
      flat.lastUpdated = new Date();
      await db.friends.put(flat);

      try {
        await wipeOldEntries();
      } catch (error) {
        captureException(error);
      }
    },
    {
      subscriptionCreatedCallback: async () => {
        await wipeAllEntries();
      },
    },
  );

  useEffect(() => {
    const handler = () => {
      reloadCentrifugeConnection();
    };
    events.addEventListener(RELOAD_EVENT, handler);

    return () => {
      events.removeEventListener(RELOAD_EVENT, handler);
    };
  }, [reloadCentrifugeConnection, events]);

  return null;
};

const LocationDataProvider: FC<{ events: EventTarget }> = ({ events }) => {
  const featuresReady = useFlagsStatus().flagsReady;
  const enableSSEWorkers = useFlag('sseWorkers');

  if (!featuresReady) {
    return null;
  } else if (enableSSEWorkers) {
    return <SSELocationDataProvider events={events} />;
  }
  return <CentrifugeLocationDataProvider events={events} />;
};

const FriendLocationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [events] = useState(() => new EventTarget());

  const useFriendsByInstanceId = (instanceId: string) =>
    useLiveQuery(async () => {
      return await db.friends.where('furalityInstanceId').equals(instanceId).toArray();
    });

  const useFriendsByEventId = (eventId: string) =>
    useLiveQuery(async () => {
      return await db.friends.where('furalityEventId').equals(eventId).toArray();
    });

  const useActiveFriends = () =>
    useLiveQuery(async () => {
      return await db.friends.orderBy('furalityInstanceId').toArray();
    });

  const reloadConnection = () => {
    events.dispatchEvent(new Event(RELOAD_EVENT));
  };

  const providerValue: FriendLocationInteraction = {
    useFriendsByInstanceId,
    useFriendsByEventId,
    useActiveFriends,
    reloadConnection,
  };

  return (
    <FriendLocationContext.Provider value={providerValue}>
      <LocationDataProvider events={events} />
      {children}
    </FriendLocationContext.Provider>
  );
};

export const organizeFriendsByEvent = (friends: FriendStreamLocation[]) => {
  const eventIdFriends: Record<string, FriendStreamLocation[]> = {};
  for (const friend of friends) {
    if (!friend.furalityEventId) {
      continue;
    }
    if (!(friend.furalityEventId in eventIdFriends)) {
      eventIdFriends[friend.furalityEventId] = [];
    }

    eventIdFriends[friend.furalityEventId].push(friend);
  }

  return Object.entries(eventIdFriends)
    .map(([eventId, eventFriends]) => ({
      eventId,
      friends: eventFriends,
    }))
    .sort((a, b) => a.friends.length - b.friends.length);
};

export default FriendLocationProvider;
